/* =======================================================================
   Framing Upgrade
   -----------------------------------------------------------------------
   Adds a "Frame It" upgrade flow to the storefront:

     1) Eligibility detection by product title (Photo/Photograph/Poster +
        an allowed dimension, OR Jersey). Items already containing
        "Frame/Framed/Framing" are excluded.

     2) Product-page popup after a successful add-to-cart asking the
        customer if they want a framing upgrade. Shown at most once per
        variant per session.

     3) Cart-page "Frame It" button injected next to each eligible line
        item. Clicking it adds the matching framing variant via
        /cart/add.js with line-item-properties linking it to the parent
        line.

     4) Custom display of framing upgrade lines in the cart so they read
        as e.g. "Framing Upgrade — Jerry Rice 8x10 Photo".

     5) Auto-removal of any framing upgrade whose parent line is no
        longer in the cart (orphan cleanup), and pre-removal when a
        customer explicitly removes a parent line from the cart.

   This file is intentionally self-contained: it does NOT modify
   theme.js. It hooks the storefront via document-level event listeners,
   jQuery's ajaxComplete, and a MutationObserver on the cart table.
   ======================================================================= */

(function () {
  'use strict';

  if (!window.FramingUpgrade || !window.FramingUpgrade.config) {
    // Config snippet missing — bail silently rather than throwing.
    return;
  }

  var CONFIG = window.FramingUpgrade.config;

  // ---------------------------------------------------------------------
  // 1. Money formatting (mirrors Shopify's Liquid `money` filter for the
  //    common formats we expect: amount, amount_no_decimals, with_comma).
  // ---------------------------------------------------------------------
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || CONFIG.moneyFormat || '${{amount}}';

    function defaultOption(opt, def) { return (typeof opt === 'undefined' ? def : opt); }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  // ---------------------------------------------------------------------
  // 2. Eligibility logic.
  //    Public so it can be reused / unit-tested:
  //      window.FramingUpgrade.detect("Jerry Rice Signed 8x10 Photo")
  //        -> { eligible: true, kind: 'photo', sizeKey: '8x10', ... }
  // ---------------------------------------------------------------------
  var EXCLUDE_REGEX     = /\bframe(d|s|ing|r|rs)?\b/i;
  var JERSEY_REGEX      = /\bjerseys?\b/i;
  var PHOTO_REGEX       = /\b(photo(s|graph|graphs)?|posters?)\b/i;
  // Captures pairs like 8x10 / 8 x 10 / 8X10 / 8 by 10 / 8×10
  var DIMENSION_REGEX   = /\b(8|11|12|16)\s*(?:x|×|by)\s*(10|14|17|18|20)\b/i;
  var ALLOWED_PAIRS     = {
    '8x10': true,
    '11x14': true,
    '11x17': true,
    '12x18': true,
    '16x20': true
  };

  function detect(rawTitle) {
    var result = {
      eligible: false,
      kind: null,        // 'photo' | 'poster' | 'jersey'
      sizeKey: null,     // '8x10' | '11x14' | ... | 'jersey'
      label: null,       // human label like '8 x 10'
      priceCents: 0,
      variantId: null,
      reason: null       // string for debugging
    };

    if (!rawTitle || typeof rawTitle !== 'string') {
      result.reason = 'no-title';
      return result;
    }

    var title = rawTitle;

    // Hard exclusion: anything that already mentions a frame.
    if (EXCLUDE_REGEX.test(title)) {
      result.reason = 'excluded-frame-keyword';
      return result;
    }

    // Jersey path — flat rate, no dimension required.
    if (JERSEY_REGEX.test(title)) {
      var jerseySize = CONFIG.sizes['jersey'];
      result.eligible   = true;
      result.kind       = 'jersey';
      result.sizeKey    = 'jersey';
      result.label      = jerseySize.label;
      result.priceCents = jerseySize.priceCents;
      result.variantId  = jerseySize.variantId;
      return result;
    }

    // Photo / poster path — needs an allowed dimension.
    var photoMatch = PHOTO_REGEX.exec(title);
    if (!photoMatch) {
      result.reason = 'not-photo-or-poster';
      return result;
    }

    var dimMatch = DIMENSION_REGEX.exec(title);
    if (!dimMatch) {
      result.reason = 'no-dimension';
      return result;
    }

    var sizeKey = dimMatch[1] + 'x' + dimMatch[2];
    if (!ALLOWED_PAIRS[sizeKey]) {
      result.reason = 'dimension-not-allowed';
      return result;
    }

    var sizeDef = CONFIG.sizes[sizeKey];
    if (!sizeDef) {
      result.reason = 'no-config-for-size';
      return result;
    }

    var firstWord = photoMatch[1].toLowerCase();
    var kind = firstWord.indexOf('poster') === 0 ? 'poster' : 'photo';

    result.eligible   = true;
    result.kind       = kind;
    result.sizeKey    = sizeKey;
    result.label      = sizeDef.label;
    result.priceCents = sizeDef.priceCents;
    result.variantId  = sizeDef.variantId;
    return result;
  }

  window.FramingUpgrade.detect = detect;

  // ---------------------------------------------------------------------
  // 3. Cart helpers — fetch the live cart and inspect frame upgrades.
  //    A line is a "framing upgrade" if its variant_id matches one of
  //    our configured framing variants. The line item property
  //    `_frame_for` carries the parent line item key.
  // ---------------------------------------------------------------------
  function fetchCart() {
    return $.ajax({ url: '/cart.js', dataType: 'json' });
  }

  function isFramingUpgradeItem(item) {
    if (!item) return false;
    for (var key in CONFIG.sizes) {
      if (CONFIG.sizes.hasOwnProperty(key) && CONFIG.sizes[key].variantId === item.variant_id) {
        return true;
      }
    }
    return false;
  }

  function getFrameForKey(item) {
    if (!item || !item.properties) return null;
    return item.properties._frame_for || null;
  }

  // Returns { parentKeyToFrameLine: {...}, parentKeys: {...} }
  function indexCart(cart) {
    var frames = {};   // parent_key -> frame line
    var parents = {};  // key -> line
    if (!cart || !cart.items) return { frames: frames, parents: parents };

    cart.items.forEach(function (item) {
      if (isFramingUpgradeItem(item)) {
        var parent = getFrameForKey(item);
        if (parent) frames[parent] = item;
      } else {
        parents[item.key] = item;
      }
    });
    return { frames: frames, parents: parents };
  }

  // ---------------------------------------------------------------------
  // 4. Cart mutation API. Sequential to avoid race conditions where two
  //    cart calls fight each other.
  // ---------------------------------------------------------------------
  var cartOpQueue = $.Deferred().resolve();

  function queueCartOp(op) {
    var next = cartOpQueue.then(function () { return op(); });
    // Ensure failures don't poison the chain.
    cartOpQueue = next.then(null, function () { return $.Deferred().resolve(); });
    return next;
  }

  function addFramingUpgrade(detection, parentItem) {
    return queueCartOp(function () {
      var data = {
        id: detection.variantId,
        quantity: 1,
        properties: {
          _frame_for:           parentItem.key,
          _frame_parent_title:  parentItem.product_title,
          _frame_size:          detection.label,
          _frame_kind:          detection.kind
        }
      };
      return $.ajax({
        type: 'POST',
        url: '/cart/add.js',
        dataType: 'json',
        contentType: 'application/json',
        data: JSON.stringify(data)
      });
    });
  }

  function removeLineByKey(key) {
    return queueCartOp(function () {
      return $.ajax({
        type: 'POST',
        url: '/cart/change.js',
        dataType: 'json',
        contentType: 'application/json',
        data: JSON.stringify({ id: key, quantity: 0 })
      });
    });
  }

  // Walk the cart and remove any framing upgrades whose parent line
  // is missing. Returns a promise that resolves to the (possibly
  // updated) cart.
  function cleanupOrphans() {
    return queueCartOp(function () {
      return fetchCart().then(function (cart) {
        var idx = indexCart(cart);
        var orphanKeys = [];
        Object.keys(idx.frames).forEach(function (parentKey) {
          if (!idx.parents[parentKey]) {
            orphanKeys.push(idx.frames[parentKey].key);
          }
        });
        if (!orphanKeys.length) return cart;
        // Remove orphans one by one, then return the latest cart.
        var chain = $.Deferred().resolve();
        orphanKeys.forEach(function (k) {
          chain = chain.then(function () { return removeLineByKey(k); });
        });
        return chain.then(fetchCart);
      });
    });
  }

  // ---------------------------------------------------------------------
  // 5. The popup.
  // ---------------------------------------------------------------------
  var $modal;
  var modalConfirmHandler = null;

  function ensureModal() {
    if ($modal && $modal.length) return $modal;
    $modal = $('#FramingUpgradeModal');
    if (!$modal.length) return null;

    $modal.on('click', '[data-frame-modal-close]', closeModal);
    $modal.on('click', '[data-frame-modal-confirm]', function () {
      if (modalConfirmHandler) modalConfirmHandler();
    });
    $(document).on('keydown.framingUpgrade', function (e) {
      if (e.key === 'Escape') closeModal();
    });
    return $modal;
  }

  function openModal(detection, parentItem) {
    var $m = ensureModal();
    if (!$m) return;

    var bodyTpl;
    if (detection.kind === 'jersey')      bodyTpl = CONFIG.strings.modalBodyJersey;
    else if (detection.kind === 'poster') bodyTpl = CONFIG.strings.modalBodyPoster;
    else                                  bodyTpl = CONFIG.strings.modalBodyPhoto;

    var bodyText = bodyTpl.replace('{size}', detection.label);
    var priceText = formatMoney(detection.priceCents);
    var confirmText = CONFIG.strings.modalConfirm.replace('{price}', priceText);

    $m.find('[data-frame-modal-body]').text(bodyText);
    $m.find('[data-frame-modal-confirm]').text(confirmText);
    $m.find('.frame-modal__heading').text(CONFIG.strings.modalHeading);

    modalConfirmHandler = function () {
      closeModal();
      addFramingUpgrade(detection, parentItem)
        .done(function () { onCartChanged(); })
        .fail(function () { window.alert(CONFIG.strings.addError); });
    };

    $m.attr('aria-hidden', 'false').addClass('frame-modal--active');
    $('body').addClass('frame-modal-open');
  }

  function closeModal() {
    if (!$modal || !$modal.length) return;
    $modal.attr('aria-hidden', 'true').removeClass('frame-modal--active');
    $('body').removeClass('frame-modal-open');
    modalConfirmHandler = null;
  }

  // ---------------------------------------------------------------------
  // 6. Session-storage tracking — only prompt once per variant per
  //    session.
  // ---------------------------------------------------------------------
  function sessionKeyFor(variantId) { return 'frameUpgradePrompted:' + variantId; }

  function hasBeenPrompted(variantId) {
    try { return window.sessionStorage.getItem(sessionKeyFor(variantId)) === '1'; }
    catch (e) { return false; }
  }

  function markPrompted(variantId) {
    try { window.sessionStorage.setItem(sessionKeyFor(variantId), '1'); }
    catch (e) { /* private mode etc. */ }
  }

  // ---------------------------------------------------------------------
  // 7. Hook into add-to-cart on the product page. We listen for any
  //    successful POST to /cart/add.js via jQuery's global ajaxComplete.
  // ---------------------------------------------------------------------
  function looksLikeFramingVariant(variantId) {
    for (var key in CONFIG.sizes) {
      if (CONFIG.sizes.hasOwnProperty(key) && CONFIG.sizes[key].variantId === variantId) {
        return true;
      }
    }
    return false;
  }

  $(document).ajaxComplete(function (event, xhr, settings) {
    if (!settings || !settings.url) return;
    var url = settings.url;

    // After ANY cart mutation, run an orphan cleanup pass and refresh
    // any visible cart UI.
    if (/\/cart\/(change|update)/.test(url)) {
      // Defer slightly so the originating handler finishes its own work.
      setTimeout(function () {
        cleanupOrphans().then(onCartChanged);
      }, 0);
    }

    if (/\/cart\/add(\.js)?$/.test(url)) {
      var lineItem = null;
      try { lineItem = JSON.parse(xhr.responseText); }
      catch (e) { return; }
      if (!lineItem || !lineItem.id) return;

      // Don't prompt about the framing upgrade itself!
      if (looksLikeFramingVariant(lineItem.id)) {
        setTimeout(function () { onCartChanged(); }, 0);
        return;
      }

      // Detect eligibility from the just-added product's title.
      var title = lineItem.product_title || lineItem.title || '';
      var detection = detect(title);
      if (!detection.eligible) {
        setTimeout(function () { onCartChanged(); }, 0);
        return;
      }

      if (hasBeenPrompted(lineItem.variant_id || lineItem.id)) {
        setTimeout(function () { onCartChanged(); }, 0);
        return;
      }

      // We need the parent line item's `key` to attach `_frame_for`.
      // Pull the latest cart and find the matching line.
      fetchCart().then(function (cart) {
        var matched = null;
        // Iterate from end (most recent additions) to find the line.
        for (var i = cart.items.length - 1; i >= 0; i--) {
          if (cart.items[i].variant_id === (lineItem.variant_id || lineItem.id)) {
            matched = cart.items[i];
            break;
          }
        }
        if (!matched) return;

        // If a framing upgrade already exists for this parent, skip the prompt.
        var idx = indexCart(cart);
        if (idx.frames[matched.key]) return;

        markPrompted(matched.variant_id);
        openModal(detection, matched);
      });
    }
  });

  // ---------------------------------------------------------------------
  // 8. Cart-page rendering: inject Frame-It buttons next to eligible
  //    items, override the title for framing upgrade lines, and lock
  //    the framing-upgrade quantity.
  // ---------------------------------------------------------------------
  var cartRenderInFlight = false;

  // Optionally takes a known-fresh cart (theme.cartObject right after a
  // QtySelector rebuild, or the cart rerenderCartTable just painted) so
  // Frame It slots re-appear in the same frame instead of after a
  // network round-trip — the visible "button blinks away and back"
  // flagged in Dom's 2026-08-25 preview review.
  function renderCartUI(knownCart) {
    var $tbody = $('#CartProducts');
    if (!$tbody.length) return;

    if (cartRenderInFlight) return;
    cartRenderInFlight = true;

    var source = knownCart ? $.Deferred().resolve(knownCart).promise() : fetchCart();
    source.always(function () { /* keep flag truthy until we're done below */ })
      .then(function (cart) {
        var idx = indexCart(cart);
        // Build a key -> line map for fast lookup.
        var byKey = {};
        cart.items.forEach(function (it) { byKey[it.key] = it; });

        // Walk the rendered rows and reconcile against the live cart.
        $tbody.children('tr').each(function (i) {
          var $row = $(this);
          var $titleLink = $row.find('a.h5').first();
          var $qtyInput = $row.find('input.cart__quantity');
          // Prefer matching by key (stable), fall back to positional.
          var key = $row.attr('data-cart-line-key');
          var line = (key && byKey[key]) || cart.items[i];
          if (!line) return;

          // --- Framing upgrade line: rewrite title, lock qty -------------
          if (isFramingUpgradeItem(line)) {
            var parentTitle = (line.properties && line.properties._frame_parent_title) || '';
            var label = CONFIG.strings.lineTitlePrefix;
            if (parentTitle) label += ' \u2014 ' + parentTitle;
            $titleLink.text(label).removeAttr('href');
            // Lock quantity at 1 (visually + functionally).
            $qtyInput.attr('readonly', 'readonly').css('pointer-events', 'none');
            $row.addClass('cart__row--framing-upgrade');
            return;
          }

          // --- Regular line: maybe inject a Frame It button -------------
          $row.addClass('cart__row--frame-host');
          var $cell = $titleLink.closest('td');

          // Remove any previous slot we injected.
          $cell.find('.frame-upgrade-slot').remove();

          var detection = detect(line.product_title || '');
          if (!detection.eligible) return;

          // If a framing upgrade for this line already exists, show a
          // muted "Framing Upgrade Added" indicator instead of the button.
          var $slot = $('<div class="frame-upgrade-slot"></div>');
          if (idx.frames[line.key]) {
            $slot.html(
              '<span class="frame-upgrade-added">' +
              CONFIG.strings.buttonAdded +
              '</span>'
            );
          } else {
            var priceText = formatMoney(detection.priceCents);
            var btnText = CONFIG.strings.buttonAdd.replace('{price}', priceText);
            var $btn = $(
              '<button type="button" class="btn btn--small frame-upgrade-btn">' +
              btnText +
              '</button>'
            );
            $btn.data('frame-detection', detection);
            $btn.data('frame-parent-key', line.key);
            $slot.append($btn);
          }

          // Place the slot after the variant/properties block but before
          // the remove link. Inserting after the title link is the most
          // robust spot.
          var $remove = $cell.find('a[href*="/cart/change"]').closest('p');
          if ($remove.length) $slot.insertBefore($remove);
          else $cell.append($slot);
        });
      })
      .always(function () {
        cartRenderInFlight = false;
      });
  }

  // Click handler for injected Frame-It buttons.
  $(document).on('click', '.frame-upgrade-btn', function (e) {
    e.preventDefault();
    var $btn = $(this);
    if ($btn.hasClass('frame-upgrade-btn--loading')) return;
    var detection = $btn.data('frame-detection');
    var parentKey = $btn.data('frame-parent-key');
    if (!detection || !parentKey) return;

    // Enter the loading state. The button remains visually spinning
    // until the cart table re-renders, at which point this entire
    // button is replaced with a "Framing Upgrade Added" badge.
    $btn.addClass('frame-upgrade-btn--loading').prop('disabled', true);

    // Re-fetch cart to get latest parent line item (its `key` may have
    // changed since render if other ops happened in between).
    fetchCart().then(function (cart) {
      var parent = null;
      for (var i = 0; i < cart.items.length; i++) {
        if (cart.items[i].key === parentKey) { parent = cart.items[i]; break; }
      }
      if (!parent) {
        // Couldn't locate the parent — bail out of the loading state.
        $btn.removeClass('frame-upgrade-btn--loading').prop('disabled', false);
        return;
      }
      addFramingUpgrade(detection, parent)
        .done(function () { onCartChanged(); })
        .fail(function () {
          window.alert(CONFIG.strings.addError);
          $btn.removeClass('frame-upgrade-btn--loading').prop('disabled', false);
        });
    });
  });

  // ---------------------------------------------------------------------
  // 9. Pre-removal hook on the cart page.
  //
  //    The cart-template.liquid renders remove links as full-page
  //    navigations:
  //
  //      <a href="/cart/change?line=N&quantity=0">Remove</a>
  //
  //    We intercept those clicks, look up the parent line, remove its
  //    framing upgrade first, then remove the parent. Because each cart
  //    op runs through `queueCartOp`, the orphan can never reach the
  //    rendered cart.
  // ---------------------------------------------------------------------
  $(document).on('click', 'a[href*="/cart/change"]', function (e) {
    var href = $(this).attr('href') || '';
    var lineMatch = /[?&]line=(\d+)/.exec(href);
    var qtyMatch  = /[?&]quantity=(\d+)/.exec(href);
    if (!lineMatch || !qtyMatch || qtyMatch[1] !== '0') return;
    var lineIndex = parseInt(lineMatch[1], 10);
    e.preventDefault();

    fetchCart().then(function (cart) {
      var item = cart.items[lineIndex - 1];
      if (!item) { window.location.href = href; return; }

      // If the user is removing a framing upgrade directly, just do it.
      // (2026-08-25: finish with the live re-render instead of a full
      // reload — the reload was the "item disappears and reappears"
      // Dom flagged on the preview.)
      if (isFramingUpgradeItem(item)) {
        removeLineByKey(item.key).done(function () { onCartChanged(); });
        return;
      }

      var idx = indexCart(cart);
      var frame = idx.frames[item.key];
      var chain = $.Deferred().resolve();
      if (frame) {
        chain = chain.then(function () { return removeLineByKey(frame.key); });
      }
      chain.then(function () { return removeLineByKey(item.key); })
           .done(function () { onCartChanged(); }); // live re-render, no reload
    });
  });

  // The cart page also has a form-based "Update" button which posts a
  // full updates[] array. We don't try to intercept that — instead, the
  // ajaxComplete orphan cleanup runs after each AJAX qty change, and a
  // full-form update will land back on the cart page where the page-load
  // cleanup pass catches any orphans.

  // ---------------------------------------------------------------------
  // 10. Re-render the full cart table from a fresh cart object.
  //     Mirrors theme.js QtySelector.updateCartItemCallback so the table
  //     stays consistent with the rest of the theme's cart rendering.
  //     Called after we add or remove a framing upgrade so the new/removed
  //     line appears live without requiring a page reload.
  // ---------------------------------------------------------------------
  function rerenderCartTable(cart) {
    var $tbody = $('#CartProducts');
    if (!$tbody.length || !cart) return;
    if (typeof Handlebars === 'undefined') return;

    // Empty cart: reload so the "empty cart" state shows.
    if (cart.item_count === 0) {
      window.location.reload();
      return;
    }

    var source = $('#CartProductTemplate').html();
    if (!source) return;
    var template = Handlebars.compile(source);

    var items = [];
    cart.items.forEach(function (cartItem, index) {
      var prodImg;
      if (cartItem.image === null || typeof cartItem.image === 'undefined') {
        prodImg = '//cdn.shopify.com/s/assets/admin/no-image-medium-cc9732cb976dd349a0df1d39816fbcc7.gif';
      } else {
        prodImg = cartItem.image
          .replace(/(\.[^.]*)$/, '_240x240$1')
          .replace('http:', '');
      }

      // Strip underscore-prefixed properties from display (matches theme.js).
      var displayProps = null;
      if (cartItem.properties) {
        displayProps = {};
        Object.keys(cartItem.properties).forEach(function (k) {
          var v = cartItem.properties[k];
          if (k.charAt(0) !== '_' && v) displayProps[k] = v;
        });
      }

      var moneyFormat = (window.theme && theme.moneyFormat) || CONFIG.moneyFormat;
      var fmt = (window.theme && theme.Currency && theme.Currency.formatMoney)
        ? function (cents) { return theme.Currency.formatMoney(cents, moneyFormat); }
        : function (cents) { return formatMoney(cents, moneyFormat); };

      items.push({
        key: cartItem.key,
        line: index + 1,
        url: cartItem.url,
        img: prodImg,
        name: cartItem.product_title,
        variation: cartItem.variant_title,
        properties: displayProps,
        itemQty: cartItem.quantity,
        price: fmt(cartItem.price),
        vendor: cartItem.vendor,
        linePrice: fmt(cartItem.line_price),
        originalLinePrice: fmt(cartItem.original_line_price),
        discounts: cartItem.discounts,
        discountsApplied: cartItem.line_price !== cartItem.original_line_price
      });
    });

    // Set a flag so our MutationObserver doesn't fight this update.
    cartRenderInFlight = true;
    $tbody.empty().append(template({ items: items }));
    cartRenderInFlight = false;

    // Re-initialize QtySelectors on the new number inputs so the theme's
    // +/- buttons and qty change handlers keep working.
    if (typeof window.QtySelector === 'function') {
      $tbody.find('input[type="number"]').each(function (i, el) {
        try { new window.QtySelector($(el)); } catch (e) { /* ignore */ }
      });
    }

    // Update the subtotal. If the wholesale (WPD) span exists, update
    // just that span so the WPD price display isn't nuked; otherwise
    // replace the whole #CartSubtotal HTML (matches theme.js behavior).
    var $subtotal = $('#CartSubtotal');
    if ($subtotal.length) {
      var moneyFormat2 = (window.theme && theme.moneyFormat) || CONFIG.moneyFormat;
      var fmt2 = (window.theme && theme.Currency && theme.Currency.formatMoney)
        ? function (c) { return theme.Currency.formatMoney(c, moneyFormat2); }
        : function (c) { return formatMoney(c, moneyFormat2); };
      var $wcp = $subtotal.find('.wcp-original-cart-total');
      if ($wcp.length) {
        $wcp.html(fmt2(cart.total_price));
      } else {
        $subtotal.html(fmt2(cart.total_price));
      }
    }

    // Update the global cart object the theme keeps around.
    if (window.theme) theme.cartObject = cart;

    // Run our own reconciliation (Frame It buttons, custom titles) —
    // from the cart we just painted, so the slots land in the same frame.
    renderCartUI(cart);
  }

  // ---------------------------------------------------------------------
  // 11. Glue: re-render whenever the cart changes; observe DOM changes
  //     to survive the Handlebars re-render done by theme.js QtySelector.
  // ---------------------------------------------------------------------
  function onCartChanged() {
    if ($('#CartProducts').length) {
      // Pull latest cart and do a full re-render so new/removed lines
      // appear without a page reload.
      fetchCart().then(function (cart) {
        rerenderCartTable(cart);
      });
    }
    // Update the header cart bubble count if present.
    fetchCart().then(function (cart) {
      var $count = $('.site-header__cart-indicator');
      if ($count.length) {
        if (cart.item_count > 0) $count.removeClass('hide');
        else $count.addClass('hide');
      }
    });
  }

  function initCartObserver() {
    var node = document.getElementById('CartProducts');
    if (!node || typeof MutationObserver === 'undefined') return;
    var debounce;
    var observer = new MutationObserver(function () {
      if (cartRenderInFlight) return; // our own writes
      clearTimeout(debounce);
      debounce = setTimeout(function () {
        // Same-frame repaint from the theme's cached cart (fresh right
        // after a QtySelector rebuild), then reconcile against the live
        // cart. A resolved Deferred runs synchronously, so the flag has
        // cleared again by the time the reconcile call checks it.
        if (window.theme && window.theme.cartObject) renderCartUI(window.theme.cartObject);
        renderCartUI();
      }, 50);
    });
    observer.observe(node, { childList: true, subtree: true });
  }

  // ---------------------------------------------------------------------
  // 11. Boot.
  // ---------------------------------------------------------------------
  $(function () {
    // Page-load orphan sweep — catches anything that survived a previous
    // session (e.g. user closed the tab between removing the parent and
    // the cleanup running).
    cleanupOrphans().then(function () {
      if ($('#CartProducts').length) {
        renderCartUI();
        initCartObserver();
      }
    });
  });
})();
